import { type Dispatch } from "redux";
import { getAxiosError } from "../../../../../utils/axiosError";
import { type BasePerformanceRequestFilterParams } from "../../../Common/models";
import { sendExportRequest } from "../../../Common/utils/performanceUtils";
import DataService from "../../services/surveysDataService";
import {
  setEngagementLineData,
  reqSurveyQuestionsData,
  setSurveyQuestionsData,
  setSurveyQuestionsDataError,
  reqSelectedQuestionData,
  setSelectedQuestionData,
  setSelectedQuestionDataError,
  reqSelectedAnswerData,
  setSelectedAnswerData,
  setSelectedAnswerDataError,
  setSurveyContentData,
  setEngagementData,
  reqEngagementLineData,
  reqEngagementData,
  setEngagementLineDataError,
  setEngagementDataError,
  reqPeopleDetails,
  setPeopleDetails,
  setPeopleDetailsError,
} from "../slices/surveyPerformanceSlice";

export const fetchEngagementLineData = (surveyId: number, filterParams: BasePerformanceRequestFilterParams) => {
  return async (dispatch: Dispatch) => {
    dispatch(reqEngagementLineData());
    try {
      const data = await DataService.getSurveyEngagementLine(surveyId, filterParams);
      dispatch(
        setEngagementLineData({
          data,
        }),
      );
    } catch (err) {
      if (err.code !== "ERR_CANCELED") {
        const errorMessage = getAxiosError(err);
        dispatch(setEngagementLineDataError({ errorMessage }));
      }
    }
  };
};

export const fetchSurveyQuestionsData = (itemId: number, filterParams: BasePerformanceRequestFilterParams) => {
  return async (dispatch: Dispatch) => {
    dispatch(reqSurveyQuestionsData());

    try {
      const data = await DataService.getSurveyQuestionsData(itemId, filterParams);

      // Group questions by QuestionId and find the highest version for each
      const questionMap = new Map();
      data.forEach((question) => {
        const existingQuestion = questionMap.get(question.QuestionId);
        if (!existingQuestion || question.QuestionVersion > existingQuestion.QuestionVersion) {
          questionMap.set(question.QuestionId, question);
        }
      });

      // Convert map values back to array
      const latestVersionsOfQuestions = Array.from(questionMap.values());

      dispatch(setSurveyQuestionsData({ data: latestVersionsOfQuestions }));
    } catch (err) {
      // Request was aborted manually, no need to change anything
      if (err.code !== "ERR_CANCELED") {
        const errorMessage = getAxiosError(err);
        dispatch(setSurveyQuestionsDataError({ errorMessage }));
      }
    }
  };
};

export const fetchEngagementData = (surveyId: number, filterParams: BasePerformanceRequestFilterParams) => {
  return async (dispatch: Dispatch) => {
    dispatch(reqEngagementData());
    try {
      const data = await DataService.getSurveyEngagement(surveyId, filterParams);

      dispatch(
        setEngagementData({
          data,
        }),
      );
    } catch (err) {
      // Request was aborted manually, no need to change anything
      if (err.code !== "ERR_CANCELED") {
        const errorMessage = getAxiosError(err);
        dispatch(setEngagementDataError({ errorMessage }));
      }
    }
  };
};

export const fetchSelectedQuestionData = (
  surveyId: number,
  questionId: string,
  filterParams: BasePerformanceRequestFilterParams,
) => {
  return async (dispatch: Dispatch) => {
    dispatch(reqSelectedQuestionData());

    try {
      const data = await DataService.getSurveyQuestionDetails(surveyId, questionId, filterParams);

      // Only display the latest answer versions
      const latestAnswerVersion = data.filter(
        (item) => item.QuestionVersion === Math.max(...data.map((d) => d.QuestionVersion)),
      )[0];

      dispatch(setSelectedQuestionData({ data: latestAnswerVersion }));
    } catch (err) {
      const errorMessage = getAxiosError(err);
      dispatch(setSelectedQuestionDataError({ errorMessage }));
    }
  };
};

export const fetchSelectedAnswerData = (
  surveyId: number,
  questionId: string,
  answerId: string,
  filterParams: BasePerformanceRequestFilterParams,
) => {
  return async (dispatch: Dispatch) => {
    dispatch(reqSelectedAnswerData());

    try {
      const data = await DataService.getSurveyAnswerDetails(surveyId, questionId, answerId, filterParams);

      dispatch(setSelectedAnswerData({ data }));
    } catch (err) {
      // Request was aborted manually, no need to change anything
      if (err.code !== "ERR_CANCELED") {
        const errorMessage = getAxiosError(err);
        dispatch(setSelectedAnswerDataError({ errorMessage }));
      }
    }
  };
};

export const fetchPeopleDetails = (surveyId: number, filterParams: BasePerformanceRequestFilterParams) => {
  return async (dispatch: Dispatch) => {
    dispatch(reqPeopleDetails());

    try {
      const data = await DataService.getSurveyPeopleDetails(surveyId, filterParams);
      dispatch(setPeopleDetails({ data }));
    } catch (err) {
      if (err.code !== "ERR_CANCELED") {
        const errorMessage = getAxiosError(err);
        dispatch(setPeopleDetailsError({ errorMessage }));
      }
    }
  };
};

export const fetchOtherOptionInformation = (surveyId: number) => {
  return async (dispatch: Dispatch) => {
    try {
      const { data } = await DataService.getSurveyContentAsync(surveyId);
      dispatch(setSurveyContentData(data));
    } catch (err) {
      // For now, I think it's fine to ignore this error. Only thing we would
      // be missing is the * next to required Text Responses
      console.error(err);
    }
  };
};

export const doExport = (surveyId: number, filterParams: BasePerformanceRequestFilterParams, surveyTitle: string) => {
  let abortController = new AbortController();
  return sendExportRequest(
    (abortController) => DataService.getExport(surveyId, filterParams),
    surveyTitle,
    abortController,
  );
};
